/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {
  getDeviceIdentifierFromType
} from '@ion/core/src/utils/DeviceUtils';
import {isSelectedDeviceSCI} from '@ion/dls/src/editors/DeviceEditorUtils';
import Render from '@ion/dls/src/render/Render';
import {useSmartSearchDevice} from '@ion/query/src/hooks/useSmartSearchDevice';
import {showJobCreateEditor} from '../../components/jobs/state';
import {useDeviceViewRefresh} from '../../hooks/useDeviceViewRefresh';
import {useFilePermissionsAction} from '../../hooks/useFilePermissionsAction';
import {deviceDetailSelector} from '../../store/selectors/devices';
import BulkViewDeviceEditor from './BulkViewDeviceEditor';
import BulkViewDeviceTabs from './BulkViewDeviceTabs';
import {getViewDeviceTabs} from './helpers';
import {deviceSwapResultSelector} from './selector';
import SentinelTabView from './SentinelTabView';
import {
  auditDevices,
  editAdminDevice,
  moveAdminDevices,
  setDevicesTabs,
  showBulkViewDevices,
  swapAdminDevice,
  swapAdminDeviceConfirm,
  viewDevice
} from './state';
import {
  adminDevicesEditResultSelector,
  deviceMoveResultSelector,
  deviceSecondaryTabSelector,
  viewDevicesTabsSelector
} from './state-selectors';
import {ViewDeviceEditorProps} from './ViewDeviceEditor';

interface BulkViewDeviceContainerProps extends ViewDeviceEditorProps {
  accountId: number;
};

const useStyles = makeStyles(() => ({
  deviceTabs : {
    marginTop : '15px'
  },
  tabContentContainer : {
    minWidth  : '90%',
    minHeight : '400px'
  }
}));

/**
 * Holds dialog content for Bulk View Device.
 */
export const BulkViewDeviceContainer = ({
  accountId,
  selectedJobId,
  devicesPermissions,
  hasCreateJob,
  Features,
  includeEditAction = false,
  refreshDevice = false,
  explicitReportDate = false,
  isToggleSwitchGrid = false,
  isVbuFwEnabled = false,
  isShowColumnChooserIcon = false,
  isCtcAdmin = false,
  hasCARBEnabled = false,
  hasTeleCryptEnabled = false,
  isShowRefreshButton = true
}: BulkViewDeviceContainerProps) => {
  const localClasses     = useStyles();
  const dispatch         = useDispatch();
  const tabItems         = useSelector(viewDevicesTabsSelector);
  const editDeviceResult = useSelector(adminDevicesEditResultSelector);
  const moveDeviceResult = useSelector(deviceMoveResultSelector);
  const swapDeviceResult = useSelector(deviceSwapResultSelector);
  const deviceDetail     = useSelector(deviceDetailSelector);
  const showSecondaryTab = useSelector(deviceSecondaryTabSelector);
  const deviceIdentifier = getDeviceIdentifierFromType(deviceDetail);

  const {
    device: populatedDevice,
    refresh: onRefreshDevice
  } = useSmartSearchDevice({id: deviceDetail?.id});

  const triggerRefresh = useDeviceViewRefresh();

  const onRefresh = useCallback(() => {
    dispatch(viewDevice(populatedDevice, refreshDevice) as any);
    onRefreshDevice();
    triggerRefresh();
  }, [
    populatedDevice,
    refreshDevice
  ]);

  const onEdit = useCallback(() => {
    dispatch(moveAdminDevices(null, false));
    // Pass null since the edit dialog now pulls in the device information itself.
    dispatch(editAdminDevice(null));
  }, []);

  const onCreateJob = useCallback(() => {
    dispatch(showJobCreateEditor([populatedDevice], null, true));
  }, [populatedDevice]);

  const onMoveDevice = useCallback(() => {
    dispatch(moveAdminDevices([populatedDevice]));
  }, [populatedDevice]);

  const onAudit = useCallback(() => {
    dispatch(auditDevices(populatedDevice));
  }, [populatedDevice]);

  const onSwap = useCallback(() => {
    dispatch(moveAdminDevices(null, false));
    if (populatedDevice?.groups?.length || populatedDevice?.asset?.href || isSelectedDeviceSCI(populatedDevice)) {
      dispatch(swapAdminDeviceConfirm(populatedDevice));
    } else {
      dispatch(swapAdminDevice(populatedDevice));
    }
  }, [populatedDevice]);

  const hasFilePermissions = !!useFilePermissionsAction({Features});

  useEffect(() => {
    const selected = tabItems?.map(({data}) => data);
    if (selected?.length && (editDeviceResult || moveDeviceResult?.length || swapDeviceResult)) {
      const result = editDeviceResult || moveDeviceResult?.[0] || swapDeviceResult;
      const updated = selected.map((device) => {
        if (device?.id === result?.id) {
          device = result;
        }
        return device;
      });
      const items = getViewDeviceTabs(updated);
      dispatch(showBulkViewDevices(updated));
      dispatch(setDevicesTabs(items, result));
    }
  }, [tabItems, editDeviceResult, moveDeviceResult, swapDeviceResult]);

  return (
    <>
      <div className={localClasses.deviceTabs}>
        <BulkViewDeviceTabs onRefresh={onRefresh}
          onEdit={onEdit}
          onSwap={onSwap}
          onAudit={onAudit}
          onCreateJob={onCreateJob}
          onMoveDevice={onMoveDevice}
          populatedDevice={populatedDevice}
          devicesPermissions={devicesPermissions}
          accountId={accountId}
          hasFilePermissions={hasFilePermissions}
          hasCreateJob={hasCreateJob}
          includeEditAction={includeEditAction} />
      </div>
      <div className={localClasses.deviceTabs}>
        <div className={localClasses.tabContentContainer}>
          <BulkViewDeviceEditor onRefresh={onRefresh}
            Features={Features}
            refreshDevice={refreshDevice}
            devicesPermissions={devicesPermissions}
            populatedDevice={populatedDevice}
            deviceIdentifier={deviceIdentifier}
            hasCreateJob={hasCreateJob}
            selectedJobId={selectedJobId}
            includeEditAction={includeEditAction}
            hasCARBEnabled={hasCARBEnabled}
            hasTeleCryptEnabled={hasTeleCryptEnabled}
            isVbuFwEnabled={isVbuFwEnabled}
            explicitReportDate={explicitReportDate}
            isToggleSwitchGrid={isToggleSwitchGrid}
            isShowColumnChooserIcon={isShowColumnChooserIcon}
            isCtcAdmin={isCtcAdmin}
            isShowRefreshButton={isShowRefreshButton} />
        </div>
      </div>
      <Render.If condition={showSecondaryTab}>
        <SentinelTabView />
      </Render.If>
    </>
  );
};

export default memo(BulkViewDeviceContainer);
